// Client Portal — Article Generator portal. Extracted from App.js.
import { useState, useEffect } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Copy,
  Download,
  Edit3,
  Eye,
  FileText,
  Loader2,
  Share2,
  Sparkles,
} from "lucide-react";
import { ARTICLE_GEN_URL, getToken } from "../lib/api";
import { htmlEncode } from "../lib/utils";
import DOMPurify from "dompurify";
import { SocialShareDialog } from "../components/SocialShareDialog";

// Plain-text excerpt for a social post: strip the markdown the generator emits.
const socialExcerpt = (md, maxLen = 400) => {
  const text = (md || "")
    .replace(/^#{1,4}\s+/gm, "")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/[*_`>]/g, "")
    .replace(/^-{3,}$/gm, "")
    .replace(/\n{2,}/g, "\n\n")
    .trim();
  return text.length > maxLen ? `${text.slice(0, maxLen).trimEnd()}…` : text;
};

export const STATUS_LABELS = {
  draft: { label: "Draft", color: "bg-slate-100 text-slate-700" },
  in_review: { label: "In Review", color: "bg-amber-100 text-amber-800" },
  approved: { label: "Approved", color: "bg-green-100 text-green-800" },
};

// Markdown → HTML for the article preview. House article markdown is a minimal
// subset (headings, paragraphs, "---", "- " source lists); AI refinement may add
// basic lists/emphasis, so those are handled too. No external dependency — input
// is HTML-encoded first and the result is DOMPurify-sanitized at render time.
const articleMdInline = (s) =>
  htmlEncode(s)
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|[^*\w])\*([^*]+)\*/g, "$1<em>$2</em>")
    .replace(/`([^`]+)`/g, "<code>$1</code>");

const articleMdToHtml = (md) => {
  const html = [];
  let para = [];
  let list = null; // "ul" | "ol" | null

  const flushPara = () => {
    if (para.length) {
      html.push(`<p>${para.map(articleMdInline).join("<br>")}</p>`);
      para = [];
    }
  };
  const closeList = () => {
    if (list) {
      html.push(`</${list}>`);
      list = null;
    }
  };

  for (const raw of String(md).split("\n")) {
    const line = raw.trimEnd();
    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    const bullet = line.match(/^\s*[-*]\s+(.*)$/);
    const numbered = line.match(/^\s*\d+[.)]\s+(.*)$/);

    if (heading) {
      flushPara();
      closeList();
      const level = heading[1].length;
      html.push(`<h${level}>${articleMdInline(heading[2])}</h${level}>`);
    } else if (/^(-{3,}|\*{3,})\s*$/.test(line)) {
      flushPara();
      closeList();
      html.push("<hr>");
    } else if (bullet || numbered) {
      flushPara();
      const tag = numbered ? "ol" : "ul";
      if (list !== tag) {
        closeList();
        html.push(`<${tag}>`);
        list = tag;
      }
      html.push(`<li>${articleMdInline((numbered || bullet)[1])}</li>`);
    } else if (line.startsWith("> ")) {
      flushPara();
      closeList();
      html.push(`<blockquote>${articleMdInline(line.slice(2))}</blockquote>`);
    } else if (line === "") {
      flushPara();
      closeList();
    } else {
      closeList();
      para.push(line);
    }
  }
  flushPara();
  closeList();
  return html.join("\n");
};

// Tailwind's CDN preflight strips heading/list styles, so the preview needs
// explicit typography rules.
const ARTICLE_MD_CSS = `
.article-md-view h1 { font-size: 1.5rem; font-weight: 700; color: #0f172a; margin: 0 0 0.75rem; }
.article-md-view h2 { font-size: 1.15rem; font-weight: 600; color: #0f172a; margin: 1.5rem 0 0.5rem; }
.article-md-view h3, .article-md-view h4 { font-size: 1rem; font-weight: 600; color: #1e293b; margin: 1.25rem 0 0.5rem; }
.article-md-view p { margin: 0 0 0.875rem; line-height: 1.7; }
.article-md-view ul, .article-md-view ol { margin: 0 0 0.875rem 1.25rem; }
.article-md-view ul { list-style: disc; }
.article-md-view ol { list-style: decimal; }
.article-md-view li { margin-bottom: 0.35rem; line-height: 1.6; }
.article-md-view hr { border: none; border-top: 1px solid #e2e8f0; margin: 1.5rem 0; }
.article-md-view blockquote { border-left: 3px solid #cbd5e1; padding-left: 0.75rem; color: #475569; font-style: italic; margin: 0 0 0.875rem; }
.article-md-view code { background: #f1f5f9; padding: 0.1rem 0.3rem; border-radius: 0.25rem; font-size: 0.85em; }
`;

export const ArticleGeneratorPortal = ({ facility, user }) => {
  // phase: "list" | "form" | "generating" | "review"
  const [phase, setPhase] = useState("list");
  const [articles, setArticles] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [topic, setTopic] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // generate response
  const [articleId, setArticleId] = useState(null); // saved DB id
  const [articleMd, setArticleMd] = useState(""); // editable markdown
  const [articleStatus, setArticleStatus] = useState("draft");
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null); // "Saved" | null
  const [copied, setCopied] = useState(false);
  const [mdView, setMdView] = useState("preview"); // "preview" | "edit"
  const [refinePrompt, setRefinePrompt] = useState("");
  const [refining, setRefining] = useState(false);
  const [showShare, setShowShare] = useState(false);

  const articleAuthHeader = () => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // ── Load article list ──────────────────────────────────────────
  const loadList = () => {
    setLoadingList(true);
    fetch(`${ARTICLE_GEN_URL}/articles`, { headers: articleAuthHeader() })
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) setArticles(data);
      })
      .catch(() => {})
      .finally(() => setLoadingList(false));
  };

  useEffect(() => {
    loadList();
  }, []); // eslint-disable-line

  // ── Open saved article ─────────────────────────────────────────
  const openArticle = async (id) => {
    setError(null);
    try {
      const r = await fetch(`${ARTICLE_GEN_URL}/articles/${id}`, {
        headers: articleAuthHeader(),
      });
      if (!r.ok) throw new Error(`Error ${r.status}`);
      const data = await r.json();
      setArticleId(data.id);
      setArticleMd(data.article_md || "");
      setArticleStatus(data.status || "draft");
      setResult({ topic: data.topic, grounding: data.grounding || {} });
      setMdView("preview");
      setRefinePrompt("");
      setPhase("review");
    } catch (e) {
      setError(e.message || "Failed to load article.");
    }
  };

  // ── Generate ───────────────────────────────────────────────────
  const generate = async () => {
    if (!topic.trim()) return;
    setPhase("generating");
    setError(null);
    try {
      const r = await fetch(`${ARTICLE_GEN_URL}/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...articleAuthHeader() },
        body: JSON.stringify({
          topic: topic.trim(),
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        }),
      });
      if (r.status === 401) {
        localStorage.removeItem("hyveai_token");
        window.location.reload();
        return;
      }
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t || `Error ${r.status}`);
      }
      const data = await r.json();
      setArticleId(data.id);
      setArticleMd(data.article || "");
      setArticleStatus("draft");
      setResult({
        topic: topic.trim(),
        grounding: data.grounding || {},
        sections: data.sections,
      });
      setMdView("preview");
      setRefinePrompt("");
      setPhase("review");
      loadList();
    } catch (e) {
      setError(e.message || "Generation failed. Please try again.");
      setPhase("form");
    }
  };

  // ── Save edits ─────────────────────────────────────────────────
  const saveEdits = async (nextStatus) => {
    if (!articleId) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const body = { article_md: articleMd };
      if (nextStatus) body.status = nextStatus;
      const r = await fetch(`${ARTICLE_GEN_URL}/articles/${articleId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...articleAuthHeader() },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(`Error ${r.status}`);
      const data = await r.json();
      if (data.status) setArticleStatus(data.status);
      setSaveMsg("Saved");
      setTimeout(() => setSaveMsg(null), 2000);
      loadList();
    } catch (e) {
      setSaveMsg("Save failed");
    } finally {
      setSaving(false);
    }
  };

  // ── AI refine ──────────────────────────────────────────────────
  // Sends the CURRENT editor markdown (including unsaved manual edits)
  // plus the user's instructions; server revises, persists, and returns
  // the new markdown with status reset to draft.
  const refineArticle = async () => {
    if (!refinePrompt.trim() || !articleId || refining) return;
    setRefining(true);
    setError(null);
    try {
      const r = await fetch(`${ARTICLE_GEN_URL}/articles/${articleId}/refine`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...articleAuthHeader() },
        body: JSON.stringify({
          article_md: articleMd,
          instructions: refinePrompt.trim(),
        }),
      });
      if (r.status === 401) {
        localStorage.removeItem("hyveai_token");
        window.location.reload();
        return;
      }
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t || `Error ${r.status}`);
      }
      const data = await r.json();
      setArticleMd(data.article_md || "");
      if (data.status) setArticleStatus(data.status);
      setRefinePrompt("");
      setMdView("preview");
      loadList();
    } catch (e) {
      setError(e.message || "Refine failed. Please try again.");
    } finally {
      setRefining(false);
    }
  };

  const copyMarkdown = () => {
    navigator.clipboard.writeText(articleMd).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const downloadTxt = () => {
    const blob = new Blob([articleMd], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(result?.topic || "article")
      .replace(/[^a-z0-9]+/gi, "-")
      .toLowerCase()
      .replace(/^-|-$/g, "")}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const groundingBadge = (score) => {
    const pct = Math.round((score || 0) * 100);
    const color =
      pct >= 70
        ? "bg-green-100 text-green-800"
        : pct >= 40
          ? "bg-amber-100 text-amber-800"
          : "bg-red-100 text-red-800";
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}
      >
        Grounding: {pct}%
      </span>
    );
  };

  // ── List phase ─────────────────────────────────────────────────
  if (phase === "list") {
    return (
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Articles</h2>
            <p className="text-sm text-slate-500">
              Civics content grounded in the Liberty Tree Network KB
            </p>
          </div>
          <button
            onClick={() => {
              setTopic("");
              setNotes("");
              setError(null);
              setPhase("form");
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <FileText className="w-4 h-4" />
            <span>New Article</span>
          </button>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
            <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        )}

        {loadingList ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 text-green-500 animate-spin" />
          </div>
        ) : articles.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">
              No articles yet. Generate your first one.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
            {articles.map((a) => {
              const st = STATUS_LABELS[a.status] || STATUS_LABELS.draft;
              const grndScore = a.grounding?.score;
              return (
                <button
                  key={a.id}
                  onClick={() => openArticle(a.id)}
                  className="w-full text-left px-5 py-4 hover:bg-slate-50 transition-colors flex items-center justify-between"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {a.topic}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {new Date(a.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
                    {grndScore !== undefined && groundingBadge(grndScore)}
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.color}`}
                    >
                      {st.label}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── Form phase ─────────────────────────────────────────────────
  if (phase === "form") {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FileText className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900">
                  New Article
                </h2>
                <p className="text-sm text-slate-500">
                  Civics content grounded in the LTN knowledge base
                </p>
              </div>
            </div>
            <button
              onClick={() => setPhase("list")}
              className="flex items-center space-x-1 text-sm text-slate-500 hover:text-slate-800"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Topic <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && generate()}
                placeholder="e.g. The First Amendment and free speech protections"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Additional context{" "}
                <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Any specific angle, audience, or points to cover..."
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
              />
            </div>

            <button
              onClick={generate}
              disabled={!topic.trim()}
              className="w-full py-3 px-6 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-medium rounded-lg text-sm transition-colors"
            >
              Generate Article
            </button>
            <p className="text-xs text-slate-400 text-center">
              Generation takes 20–40 seconds. Draft is saved automatically for
              review.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Generating phase ───────────────────────────────────────────
  if (phase === "generating") {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 flex flex-col items-center text-center">
          <Loader2 className="w-10 h-10 text-green-500 animate-spin mb-4" />
          <h3 className="text-lg font-medium text-slate-800 mb-2">
            Generating article…
          </h3>
          <p className="text-sm text-slate-500 max-w-sm">
            Retrieving KB context, synthesising claims, drafting prose, and
            checking grounding. This usually takes 20–40 seconds.
          </p>
        </div>
      </div>
    );
  }

  // ── Review / edit phase ────────────────────────────────────────
  const grounding = result?.grounding || {};
  const flaggedCount = (grounding.lowConfidenceSections || []).length;
  const st = STATUS_LABELS[articleStatus] || STATUS_LABELS.draft;

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      {/* Header bar */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 px-5 py-4 flex items-center justify-between flex-wrap gap-3">
        <button
          onClick={() => {
            setPhase("list");
            setResult(null);
            setArticleMd("");
            setArticleId(null);
            setRefinePrompt("");
            setError(null);
          }}
          className="flex items-center space-x-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>All articles</span>
        </button>

        <div className="flex items-center space-x-2">
          {grounding.score !== undefined && groundingBadge(grounding.score)}
          {flaggedCount > 0 && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
              <AlertTriangle className="w-3 h-3 mr-1" />
              {flaggedCount} flagged
            </span>
          )}
          <span
            className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${st.color}`}
          >
            {st.label}
          </span>
        </div>

        <div className="flex items-center space-x-2">
          {saveMsg && (
            <span
              className={`text-xs ${saveMsg === "Saved" ? "text-green-600" : "text-red-600"}`}
            >
              {saveMsg}
            </span>
          )}
          <button
            onClick={() => saveEdits(null)}
            disabled={saving}
            className="flex items-center space-x-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Check className="w-3.5 h-3.5" />
            )}
            <span>Save Draft</span>
          </button>
          {articleStatus === "draft" && (
            <button
              onClick={() => saveEdits("in_review")}
              disabled={saving}
              className="flex items-center space-x-1.5 px-3 py-1.5 text-xs font-medium text-amber-800 bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors disabled:opacity-50"
            >
              Submit for Review
            </button>
          )}
          {articleStatus === "in_review" && (
            <button
              onClick={() => saveEdits("approved")}
              disabled={saving}
              className="flex items-center space-x-1.5 px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50"
            >
              Approve
            </button>
          )}
          {articleStatus === "approved" && articleId && (
            <button
              onClick={() => setShowShare(true)}
              className="flex items-center space-x-1.5 px-3 py-1.5 text-xs font-medium text-white bg-sky-600 hover:bg-sky-700 rounded-lg transition-colors"
              title="Publish this article to connected social accounts"
            >
              <Share2 className="w-3.5 h-3.5" />
              <span>Share</span>
            </button>
          )}
          <button
            onClick={copyMarkdown}
            className="flex items-center space-x-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
          >
            {copied ? (
              <Check className="w-3.5 h-3.5 text-green-600" />
            ) : (
              <Copy className="w-3.5 h-3.5" />
            )}
            <span>{copied ? "Copied!" : "Copy"}</span>
          </button>
          <button
            onClick={downloadTxt}
            className="flex items-center space-x-1.5 px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Topic heading */}
      {result?.topic && (
        <h1 className="text-2xl font-bold text-slate-900 px-1">
          {result.topic}
        </h1>
      )}

      {showShare && (
        <SocialShareDialog
          facility={facility}
          content={{
            content_id: `article-${articleId}`,
            kind: "text",
            title: result?.topic || "",
            body: socialExcerpt(articleMd),
          }}
          defaultBody={socialExcerpt(articleMd)}
          onClose={() => setShowShare(false)}
        />
      )}

      {/* Grounding warning */}
      {grounding.passed === false && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 flex items-start space-x-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium mb-1">Editorial review required</p>
            <p>
              Grounding score below 70%. Some sections may rely on general
              knowledge rather than the KB. Edit before approving.
            </p>
          </div>
        </div>
      )}

      {/* Refine / regenerate error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}

      {/* Article — rendered preview / markdown editor */}
      <style>{ARTICLE_MD_CSS}</style>
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide">
            Article
          </label>
          <div className="flex items-center bg-slate-100 rounded-lg p-0.5">
            <button
              onClick={() => setMdView("preview")}
              className={`flex items-center space-x-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                mdView === "preview"
                  ? "bg-white text-slate-800 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Eye className="w-3.5 h-3.5" />
              <span>Preview</span>
            </button>
            <button
              onClick={() => setMdView("edit")}
              className={`flex items-center space-x-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                mdView === "edit"
                  ? "bg-white text-slate-800 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Edit3 className="w-3.5 h-3.5" />
              <span>Edit Markdown</span>
            </button>
          </div>
        </div>
        {mdView === "preview" ? (
          articleMd.trim() ? (
            <div
              className="article-md-view text-sm text-slate-700"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(articleMdToHtml(articleMd)),
              }}
            />
          ) : (
            <p className="text-sm text-slate-400">
              Article content will appear here after generation…
            </p>
          )
        ) : (
          <textarea
            value={articleMd}
            onChange={(e) => setArticleMd(e.target.value)}
            rows={Math.max(20, articleMd.split("\n").length + 2)}
            className="w-full text-sm text-slate-700 leading-relaxed border border-slate-200 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-green-500 resize-y font-mono"
            placeholder="Article content will appear here after generation…"
          />
        )}
      </div>

      {/* Refine with AI */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
        <label className="flex items-center space-x-1.5 text-xs font-medium text-slate-500 mb-2 uppercase tracking-wide">
          <Sparkles className="w-3.5 h-3.5 text-green-600" />
          <span>Refine with AI</span>
        </label>
        <textarea
          value={refinePrompt}
          onChange={(e) => setRefinePrompt(e.target.value)}
          disabled={refining}
          rows={2}
          placeholder="Describe the changes you want — e.g. shorten the introduction, make the tone more conversational, expand the section on ballot access…"
          className="w-full text-sm text-slate-700 border border-slate-200 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none disabled:bg-slate-50 disabled:text-slate-400"
        />
        <div className="flex items-center justify-between mt-2">
          <p className="text-xs text-slate-400">
            Regenerates from the current article, including your manual edits.
            The result returns to Draft status for review.
          </p>
          <button
            onClick={refineArticle}
            disabled={refining || !refinePrompt.trim()}
            className="flex items-center space-x-1.5 px-4 py-2 text-xs font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-slate-300 disabled:cursor-not-allowed rounded-lg transition-colors flex-shrink-0 ml-4"
          >
            {refining ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Regenerating…</span>
              </>
            ) : (
              <>
                <Sparkles className="w-3.5 h-3.5" />
                <span>Regenerate</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Citation gaps */}
      {(grounding.citationGaps || []).length > 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl px-5 py-4 text-sm text-slate-600">
          <p className="font-medium text-slate-700 mb-1">
            {grounding.citationGaps.length} claim
            {grounding.citationGaps.length !== 1 ? "s" : ""} without citations
          </p>
          <p className="text-xs">
            Verify these independently or add citations before approving.
          </p>
        </div>
      )}

      {/* Bottom save bar */}
      <div className="flex justify-end space-x-2 pb-4">
        <button
          onClick={() => saveEdits(null)}
          disabled={saving}
          className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Check className="w-4 h-4" />
          )}
          <span>Save Draft</span>
        </button>
        <button
          onClick={downloadTxt}
          className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
        >
          <Download className="w-4 h-4" />
          <span>Export .md</span>
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// SERVICE ICONS
// ═══════════════════════════════════════════════════════════════════

// Keep in sync with the CANONICAL SERVICE_TYPES list in the admin portal
// (src/constants.js) — separate builds, so the list is duplicated here.
